import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';

function SalePaymentWise() {
    const [data, setData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const tableRef = React.createRef();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [paymentType, setPaymentType] = useState('0');
    const currentDate = new Date();
    const formattedDate = currentDate.toISOString().split('T')[0];
    const monthstartdate = new Date();
    monthstartdate.setDate(1);
    const formattedStartDate = monthstartdate.toISOString().split('T')[0];

    const { isAuthenticated, companyId, permissions, token, logout } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(formattedStartDate);
        setToDate(formattedDate);
        fetchTableData();
    }, [isAuthenticated, companyId, token, logout]);

    const hasReadReportPermission = permissions.includes('Read Reports');

    const fetchTableData = async () => {
        if (hasReadReportPermission) {
            try {
                const response = await fetchData('ReportApi', `/SalePaymentWise/${companyId}/${fromDate}/${toDate}/${paymentType}`, token);
                if (response.responseCode === '1111') {
                    setData(response.response_data);
                }
            } catch (error) {
                console.error('Error fetching Sale Register Details:', error);
            }
        }
    };

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        printWindow.document.write('<html><head><title>Sale Register</title></head><body>');
        printWindow.document.write('<h3 style="font-family: Times New Roman; font-size: 32px;">Sale Details (Payment Type Wise)</h3>');
        printWindow.document.write(`<h4 style="font-family: Times New Roman; font-size: 22px;">From : ${formatDate(fromDate)} To : ${formatDate(toDate)}</h4>`);
        printWindow.document.write('<table style="border-collapse: collapse; width: 100%;">');
        printWindow.document.write('<thead style="background-color: #f3f3f3;">');
        printWindow.document.write('<tr>');
        printWindow.document.write('<th style="border: 1px solid #ddd; font-size: 19px; font-weight: bolder; color: black;">Sl.No</th>');
        printWindow.document.write('<th style="border: 1px solid #ddd; font-size: 19px; font-weight: bolder; color: black;">Bill No</th>');
        printWindow.document.write('<th style="border: 1px solid #ddd; font-size: 19px; font-weight: bolder; color: black;">Bill Date</th>');
        printWindow.document.write('<th style="border: 1px solid #ddd; font-size: 19px; font-weight: bolder; color: black;">Payment Type</th>');
        printWindow.document.write('<th style="border: 1px solid #ddd; font-size: 19px; font-weight: bolder; color: black;">Table Name</th>');
        printWindow.document.write('<th style="border: 1px solid #ddd; font-size: 19px; font-weight: bolder; color: black;">Amount</th>');
        printWindow.document.write('</tr>');
        printWindow.document.write('</thead>');
        printWindow.document.write('<tbody>');

        filteredData.forEach((rowData, index) => {
            printWindow.document.write('<tr>');
            printWindow.document.write(`<td style="border: 1px solid #ddd; font-size: 16px;">${index + 1}</td>`);
            printWindow.document.write(`<td style="border: 1px solid #ddd; font-size: 16px;">${rowData.billno}</td>`);
            printWindow.document.write(`<td style="border: 1px solid #ddd; font-size: 16px;">${formatDate(rowData.billdate)}</td>`);
            printWindow.document.write(`<td style="border: 1px solid #ddd; font-size: 16px;">${rowData.paymenttype}</td>`);
            printWindow.document.write(`<td style="border: 1px solid #ddd; font-size: 16px;">${rowData.tablename}</td>`);
            printWindow.document.write(`<td style="border: 1px solid #ddd; font-size: 16px;">${rowData.amount}</td>`);
            printWindow.document.write('</tr>');
        });

        // Add the total row to the print
        printWindow.document.write('<tr class="total-row">');
        printWindow.document.write('<td colspan="5" style="border: 1px solid #ddd; font-size: 16px; text-align: right; font-weight: bolder; color: black;">Total Amount</td>');
        printWindow.document.write(`<td style="border: 1px solid #ddd; font-size: 16px;">${calculateTotalAmount()}</td>`);
        printWindow.document.write('</tr>');

        printWindow.document.write('</tbody></table></body></html>');
        printWindow.document.close();
        printWindow.print();
    };

    const calculateTotalAmount = () => {
        return filteredData.reduce((total, rowData) => total + parseFloat(rowData.amount), 0).toFixed(2);
    };

    const handleExportExcel = () => {
        const excelData = [];
        const headerCaption = [`Sale Details (Payment Type Wise) From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`];
        excelData.push(headerCaption);

        const headerRow = ['Sl.No', 'Bill No', 'Bill Date', 'Payment Type', 'Table Name', 'Amount'];
        excelData.push(headerRow);

        filteredData.forEach((rowData, index) => {
            const dataRow = [index + 1, rowData.billno, formatDate(rowData.billdate), rowData.paymenttype, rowData.tablename, rowData.amount];
            excelData.push(dataRow);
        });

        // Add the total row to the excelData
        const totalRow = ['Total Amount', '', '', '', '', calculateTotalAmount()];
        excelData.push(totalRow);

        const ws = XLSX.utils.aoa_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sale Details');
        XLSX.writeFile(wb, 'SaleDetails.xlsx');
    };
    const [isFocused, setIsFocused] = useState(false);

    return (
        <div className="w-full max-w-sm container mt-6 mx-auto">
            {/* <div className='w-full mb-2 flex'>
                <h3 className='psheading flex-grow linkcolor' style={{ fontFamily: 'Times New Roman', fontSize: '32px' }}>Sale Deatils</h3>
                <div className="mr-2" style={{ maxWidth: '200px' }}>
                    <select
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        value={paymentType}
                        onChange={(e) => setPaymentType(e.target.value)}
                    >
                        <option value="0">All</option>
                        <option value="1">Cash</option>
                        <option value="2">Card</option>
                        <option value="3">UPI</option>
                    </select>
                </div>
            </div>
            <div className='w-full mb-2 flex'>


                <div className="mr-2" style={{ maxWidth: '150px' }}>
                    <label className="block tracking-wide text-white text-xs font-bold mb-2" htmlFor="fromDate" style={{ fontSize: '16px' }}>
                        FromDate
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}

                    />
                </div>

                <div className="mr-2" style={{ maxWidth: '150px' }}>
                    <label className="block tracking-wide text-white text-xs font-bold mb-2" htmlFor="toDate" style={{ fontSize: '16px' }}>
                        ToDate
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}

                    />
                </div>


            </div>
            <div className='w-full mb-2 flex'>
                <button
                    onClick={fetchTableData}
                    style={{
                        marginLeft: '10px',
                        cursor: 'pointer',
                        backgroundColor: '#00D100',
                        color: 'white',
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '5px',
                        fontSize: '16px',
                        height: '45px',
                    }}
                >
                    Refresh
                </button>
                <button
                    onClick={handleExportExcel}
                    style={{
                        marginLeft: '10px',
                        cursor: 'pointer',
                        backgroundColor: '#009688',
                        color: 'white',
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '5px',
                        fontSize: '16px',
                        height: '45px',
                    }}
                >
                    Excel
                </button>

                <button
                    onClick={handlePrint}
                    style={{
                        marginLeft: '10px',
                        cursor: 'pointer',
                        backgroundColor: '#ff5733',
                        color: 'white',
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '5px',
                        fontSize: '16px',
                        height: '45px',
                    }}
                >
                    Print
                </button>
                <div style={{ marginBottom: '10px', marginLeft: '20px', display: 'flex', alignItems: 'center' }}>

                    <SearchIcon onClick={toggleSearchInput} style={{ cursor: 'pointer' }} />
                    {showSearchInput && (
                        <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={handleSearch}
                            style={{ marginLeft: '10px' }}
                        />
                    )}
                </div>
            </div> */}


            <div className='w-full mb-2 flex'>
                <h5 className='psheading flex-grow linkcolor' style={{ fontFamily: 'Times New Roman', color: 'black' }}>SALE DETAILS</h5>
                <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                    <div className="mr-2" style={{ maxWidth: '200px', marginTop: '15px' }}>
                        <select
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                            value={paymentType}
                            onChange={(e) => setPaymentType(e.target.value)}
                        >
                            <option value="0">All</option>
                            <option value="1">Cash</option>
                            <option value="2">Card</option>
                            <option value="3">UPI</option>
                        </select>
                    </div>
                    <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>

                        <input
                            className="shadow text-gray-700 leading-tight"
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={handleSearch}
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => setIsFocused(false)}
                            style={{
                                width: isFocused ? '300px' : '200px',
                                height: '40px',
                                border: '1px solid #ccc',
                                borderRadius: '15px',
                                padding: '0 20px',
                                transition: 'width 0.4s ease-in-out, border-color 0.3s ease-in-out',
                                outline: 'none',
                                borderColor: isFocused ? 'darkcyan' : '#ccc',
                                boxShadow: isFocused ? '0 4px 8px rgba(0, 0, 0, 0.2)' : 'none',
                                marginTop: '22px'
                            }}
                        />
                    </div>
                </div>
                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                        FromDate
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}

                    />
                </div>
                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                        ToDate
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}

                    />
                </div>
                <Button
                    style={{ color: "black", height: '36px', marginLeft: '10px', marginTop: '20px' }}
                    onClick={fetchTableData}
                    className="linkcolor"
                >

                    <span style={{ color: 'darkgreen', fontWeight: 'bolder', marginTop: '-5px' }}>REFRESH</span>
                </Button>

                <Button
                    style={{ color: "black", height: '36px', marginLeft: '10px', marginTop: '20px' }}
                    onClick={handleExportExcel}
                    className="linkcolor"
                >

                    <span style={{ color: 'darkblue', fontWeight: 'bolder', marginTop: '-5px' }}>EXCEL</span>
                </Button>

                <Button
                    style={{ color: "black", height: '36px', marginLeft: '10px', marginTop: '20px' }}
                    onClick={handlePrint}
                    className="linkcolor"
                >

                    <span style={{ color: 'orange', fontWeight: 'bolder', marginTop: '-5px' }}>PRINT</span>
                </Button>


            </div>

            <div className='w-full mb-5 flex' style={{ maxHeight: '450px', overflowY: 'auto' }}>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#f3f3f3' }}>
                            <TableRow>
                                <TableCell sx={{ borderBottom: '1px solid #ddd', fontSize: '16px', fontWeight: 'bolder', color: 'black' }}>Sl.No</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #ddd', fontSize: '16px', fontWeight: 'bolder', color: 'black' }}>Bill No</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #ddd', fontSize: '16px', fontWeight: 'bolder', color: 'black' }}>Bill Date</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #ddd', fontSize: '16px', fontWeight: 'bolder', color: 'black' }}>Payment Type</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #ddd', fontSize: '16px', fontWeight: 'bolder', color: 'black' }}>Table Name</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #ddd', fontSize: '16px', fontWeight: 'bolder', color: 'black' }}>Amount</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData.map((rowData, index) => (
                                <TableRow key={index} className={rowData.total ? 'total-row' : ''}>
                                    <TableCell sx={{ fontSize: '15px', height: '30px', padding: '11px' }}>{index + 1}</TableCell>
                                    <TableCell sx={{ fontSize: '15px', height: '30px', padding: '11px' }}>{rowData.billno}</TableCell>
                                    <TableCell sx={{ fontSize: '15px', height: '30px', padding: '11px' }}>{formatDate(rowData.billdate)}</TableCell>
                                    <TableCell sx={{ fontSize: '15px', height: '30px', padding: '11px' }}>{rowData.paymenttype}</TableCell>
                                    <TableCell sx={{ fontSize: '15px', height: '30px', padding: '11px' }}>{rowData.tablename}</TableCell>
                                    <TableCell sx={{ fontSize: '15px', height: '30px', padding: '11px' }}>{rowData.amount}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow className='total-row'>
                                <TableCell colSpan={5} sx={{ fontSize: '16px', textAlign: 'right', fontWeight: 'bolder', color: 'black', padding: '11px' }}>Total Amount</TableCell>
                                <TableCell sx={{ fontSize: '15px', height: '30px', padding: '11px' }}>{calculateTotalAmount()}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        </div>
    );
}

export default SalePaymentWise;
